function slugifySegment(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function titleCaseSlug(slug = "") {
  return slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function isAllIndiaLocation(label) {
  return typeof label === "string" && /^all\s*india$/i.test(label.trim());
}

export function buildChefMarketplacePath({ location = "", cuisines = [] } = {}) {
  const trimmedLocation = location?.trim() || "";
  const normalizedCuisines = Array.isArray(cuisines)
    ? cuisines.map((item) => String(item).trim()).filter(Boolean)
    : [];

  let path = "/cooks/chef";
  const params = new URLSearchParams();

  if (trimmedLocation && !isAllIndiaLocation(trimmedLocation)) {
    path += `/in-${slugifySegment(trimmedLocation)}`;
    if (normalizedCuisines.length === 1) {
      path += `/${slugifySegment(normalizedCuisines[0])}`;
    } else if (normalizedCuisines.length > 1) {
      params.set("cuisines", normalizedCuisines.join(","));
    }
  } else if (normalizedCuisines.length === 1) {
    path += `/cuisine-${slugifySegment(normalizedCuisines[0])}`;
  } else if (normalizedCuisines.length > 1) {
    params.set("cuisines", normalizedCuisines.join(","));
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function parseChefMarketplaceRoute(pathname, searchParams) {
  const basePath = "/cooks/chef";
  let location = "";
  let cuisines = [];

  const remainder = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length).replace(/^\//, "")
    : "";
  const parts = remainder.split("/").filter(Boolean);

  if (parts[0]?.startsWith("in-")) {
    location = titleCaseSlug(parts[0].slice(3));
    if (parts[1]) {
      cuisines = [titleCaseSlug(parts[1])];
    }
  } else if (parts[0]?.startsWith("cuisine-")) {
    cuisines = [titleCaseSlug(parts[0].slice(8))];
  }

  const cuisineQuery =
    searchParams
      ?.get("cuisines")
      ?.split(",")
      .map((item) => item.trim())
      .filter(Boolean) || [];

  if (cuisineQuery.length > 0) {
    cuisines = cuisineQuery;
  }

  const legacyCity = searchParams?.get("city")?.trim() || "";
  const legacyLocation = searchParams?.get("location")?.trim() || "";
  if (legacyLocation) location = legacyLocation;
  if (!location && legacyCity) location = legacyCity;

  return {
    location,
    cuisines,
  };
}

export function buildChefMarketplaceSeo({ location = "", cuisines = [] } = {}) {
  const trimmedLocation = location?.trim() || "";
  const normalizedCuisines = Array.isArray(cuisines)
    ? cuisines.map((item) => String(item).trim()).filter(Boolean)
    : [];
  const cuisineLabel = normalizedCuisines.join(", ");
  const path = buildChefMarketplacePath({ location, cuisines: normalizedCuisines });
  const canonical = `https://cookandchef.in${path}`;

  if (trimmedLocation && cuisineLabel) {
    return {
      title: `Hire ${cuisineLabel} Chefs in ${trimmedLocation} | CookandChef`,
      description: `Browse verified ${cuisineLabel} chefs in ${trimmedLocation}. Hire restaurant, hotel, and kitchen talent directly on CookandChef.`,
      canonical,
    };
  }

  if (trimmedLocation) {
    return {
      title: `Hire Chefs in ${trimmedLocation} | CookandChef`,
      description: `Find and hire chefs in ${trimmedLocation} for restaurants, hotels, cafés, and cloud kitchens on CookandChef.`,
      canonical,
    };
  }

  if (cuisineLabel) {
    return {
      title: `Hire ${cuisineLabel} Chefs in India | CookandChef`,
      description: `Explore ${cuisineLabel} chef profiles across India. Connect with skilled culinary professionals for your kitchen.`,
      canonical,
    };
  }

  return {
    title: "Browse Top Chefs in India | CookandChef Platform",
    description:
      "Explore verified, job-seeking chef profiles across India. Hire culinary professionals directly with zero agency commissions.",
    canonical: "https://cookandchef.in/cooks/chef",
  };
}

const WEAK_ABOUT_PATTERN =
  /^(hello|hi|hey|ok|okay|test|na|n\/a|nil|none|\.+|-+|…+)$/i;

export function formatExperienceYears(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return `${value} Years`;
  const rounded = Math.round(numeric);
  return `${rounded} ${rounded === 1 ? "Year" : "Years"}`;
}

export function getMeaningfulAbout(text, max = 120) {
  const trimmed = String(text || "").trim().replace(/\s+/g, " ");
  if (!trimmed || trimmed.length < 12 || WEAK_ABOUT_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed.length > max ? `${trimmed.slice(0, max).trim()}…` : trimmed;
}

export function takeUniqueTags(values = [], limit = 3) {
  const seen = new Set();
  const tags = [];
  for (const value of values) {
    const tag = String(value || "").trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tags.push(tag);
    if (tags.length >= limit) break;
  }
  return tags;
}
